import { FC, ReactNode, useLayoutEffect, useMemo, useRef } from 'react';

import {
    AI_CHAT_MESSAGE_ROLE,
    isAiChatFiltersMessage,
    isAiChatManualMessage,
    isAiChatQueryResultMessage,
    isAiChatUserMessage,
} from '../../models/ai-chat-message';
import { styled } from '../../theme';
import {
    AgeGroupsOrder,
    AiChatAppliedFilterSet,
    AiChatFeedbackReaction,
    AiChatFilterSet,
    AiChatHistoryMessage,
} from '../../types';
import {
    ParsedHistoryAnswer,
    hasFeedbackDetails,
    parseHistoryAnswer,
    parseManualFiltersContent,
} from '../../utility/ai-chat-content';
import { getThreadManualFiltersChips } from '../../utility/ai-chat-manual-filters';
import FlexColumn from '../FlexColumn';
import DataCollectionPicker from './DataCollectionPicker';
import FeedbackBubble from './FeedbackBubble';
import FiltersCard from './FiltersCard';
import MessageBubble from './MessageBubble';

const ThreadRoot = styled(FlexColumn)({
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    gap: 10,
    width: '100%',
});

const AssistantMessageGroup = styled(FlexColumn)({
    gap: 8,
    width: '100%',
});

const BUBBLE_MAX_WIDTH_BY_MODE = {
    split: 360,
    full: 693,
} as const;

type ThreadEntry = { message: AiChatHistoryMessage } & (
    | { role: typeof AI_CHAT_MESSAGE_ROLE.USER }
    | { role: typeof AI_CHAT_MESSAGE_ROLE.MANUAL; manual: AiChatFilterSet }
    | ({ role: typeof AI_CHAT_MESSAGE_ROLE.ASSISTANT } & ParsedHistoryAnswer)
);

const parseThread = (messages: AiChatHistoryMessage[]): ThreadEntry[] =>
    messages.flatMap((message, index): ThreadEntry[] => {
        if (isAiChatUserMessage(message)) {
            return [{ message, role: AI_CHAT_MESSAGE_ROLE.USER }];
        }

        if (isAiChatManualMessage(message)) {
            const manual = parseManualFiltersContent(message);
            return manual ? [{ message, role: AI_CHAT_MESSAGE_ROLE.MANUAL, manual }] : [];
        }

        const answer = parseHistoryAnswer(messages, index);
        return answer ? [{ message, role: AI_CHAT_MESSAGE_ROLE.ASSISTANT, ...answer }] : [];
    });

const isManualEntry = (entry: ThreadEntry): boolean => entry.role === AI_CHAT_MESSAGE_ROLE.MANUAL;

const isUnansweredPickerEntry = (entry: ThreadEntry): boolean =>
    entry.role === AI_CHAT_MESSAGE_ROLE.ASSISTANT &&
    entry.picker != null &&
    entry.picker.pickedDataCollectionId == null;

const getEntryAppliedFilters = (entry: ThreadEntry): AiChatAppliedFilterSet | null => {
    if (entry.role === AI_CHAT_MESSAGE_ROLE.MANUAL) {
        return entry.manual;
    }

    if (entry.role !== AI_CHAT_MESSAGE_ROLE.ASSISTANT || isUnansweredPickerEntry(entry)) {
        return null;
    }

    const { parsed } = entry;

    return isAiChatFiltersMessage(parsed) || isAiChatQueryResultMessage(parsed) ? parsed : null;
};

interface AiChatThreadProps {
    messages: AiChatHistoryMessage[];
    isSplitMode?: boolean;
    ariaLabel?: string;
    highlightedMessageId?: string;
    ageGroupsOrder?: AgeGroupsOrder | null;
    dataCollectionNamesById?: Record<number, string>;
    renderFilterActions?: (reaction?: AiChatFeedbackReaction | null) => ReactNode;
}

const AiChatThread: FC<AiChatThreadProps> = ({
    messages,
    isSplitMode = false,
    ariaLabel,
    highlightedMessageId,
    ageGroupsOrder,
    dataCollectionNamesById,
    renderFilterActions,
}) => {
    const highlightedRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        highlightedRef.current?.scrollIntoView({ block: 'center' });
    }, [highlightedMessageId, messages]);

    const bubbleMaxWidth = BUBBLE_MAX_WIDTH_BY_MODE[isSplitMode ? 'split' : 'full'];
    const entries = useMemo(() => parseThread(messages), [messages]);
    const manualChips = useMemo(
        () => getThreadManualFiltersChips(entries, getEntryAppliedFilters, isManualEntry),
        [entries]
    );

    return (
        <ThreadRoot role="log" aria-label={ariaLabel}>
            {entries.map((entry, index) => {
                const { message } = entry;
                const key = message.id ?? index;
                const isHighlighted = !!highlightedMessageId && message.id === highlightedMessageId;
                const highlightReaction = isHighlighted ? message.feedback?.reaction : undefined;
                const ref = isHighlighted ? highlightedRef : undefined;

                if (entry.role === AI_CHAT_MESSAGE_ROLE.USER) {
                    return (
                        <MessageBubble
                            key={key}
                            ref={ref}
                            variant={AI_CHAT_MESSAGE_ROLE.USER}
                            isSplitMode={isSplitMode}
                        >
                            {message.content}
                        </MessageBubble>
                    );
                }

                if (entry.role === AI_CHAT_MESSAGE_ROLE.MANUAL) {
                    return (
                        <FiltersCard
                            key={key}
                            ref={ref}
                            dataTypes={entry.manual.dataTypes}
                            filters={manualChips.get(entry) ?? entry.manual.filters}
                            ageGroupsOrder={ageGroupsOrder}
                            isManual
                        />
                    );
                }

                const { parsed, picker } = entry;
                const feedback = message.feedback;
                const showFeedbackBubble = !!feedback && hasFeedbackDetails(feedback);
                const isUnansweredPicker = isUnansweredPickerEntry(entry);
                const pickerBubble = picker && (
                    <MessageBubble
                        ref={isUnansweredPicker ? ref : undefined}
                        variant={AI_CHAT_MESSAGE_ROLE.ASSISTANT}
                        isSplitMode={isSplitMode}
                        highlightReaction={isUnansweredPicker ? highlightReaction : undefined}
                        isFullWidth
                    >
                        <DataCollectionPicker
                            content={picker.content}
                            options={picker.options}
                            pickedDataCollectionId={picker.pickedDataCollectionId}
                            isDismissed={picker.isDismissed}
                        />
                    </MessageBubble>
                );

                if (isUnansweredPicker) {
                    return (
                        <AssistantMessageGroup key={key}>
                            {pickerBubble}
                            {showFeedbackBubble && (
                                <FeedbackBubble feedback={feedback} maxWidth={bubbleMaxWidth} />
                            )}
                        </AssistantMessageGroup>
                    );
                }

                if (isAiChatFiltersMessage(parsed) || isAiChatQueryResultMessage(parsed)) {
                    const dataCollectionName =
                        message.data_collection_id == null
                            ? undefined
                            : dataCollectionNamesById?.[message.data_collection_id];

                    return (
                        <AssistantMessageGroup key={key}>
                            {pickerBubble}
                            <FiltersCard
                                ref={ref}
                                dataTypes={parsed.dataTypes}
                                filters={parsed.filters}
                                ageGroupsOrder={ageGroupsOrder}
                                highlightReaction={highlightReaction}
                                dataCollectionName={dataCollectionName}
                                actions={renderFilterActions?.(message.feedback?.reaction)}
                            />
                            {showFeedbackBubble && (
                                <FeedbackBubble
                                    feedback={feedback}
                                    maxWidth={BUBBLE_MAX_WIDTH_BY_MODE.split}
                                />
                            )}
                        </AssistantMessageGroup>
                    );
                }

                return (
                    <AssistantMessageGroup key={key}>
                        <MessageBubble
                            ref={ref}
                            variant={AI_CHAT_MESSAGE_ROLE.ASSISTANT}
                            isSplitMode={isSplitMode}
                            highlightReaction={highlightReaction}
                        >
                            {parsed.content}
                        </MessageBubble>
                        {showFeedbackBubble && (
                            <FeedbackBubble feedback={feedback} maxWidth={bubbleMaxWidth} />
                        )}
                    </AssistantMessageGroup>
                );
            })}
        </ThreadRoot>
    );
};

export default AiChatThread;
